import * as React from 'react'

import { ThemeContext, type Theme, type ThemePreference } from '@/app/providers/theme-context'

const STORAGE_KEY = 'sensei:theme'

function getStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored
  } catch { /* ignore */ }
  return 'dark'
}

function resolveTheme(pref: ThemePreference): Theme {
  if (pref !== 'auto') return pref
  const hour = new Date().getHours()
  return hour >= 7 && hour < 18 ? 'light' : 'dark'
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(getStoredPreference)
  const [theme, setTheme] = React.useState<Theme>(() => resolveTheme(preference))

  React.useEffect(() => {
    const resolved = resolveTheme(preference)
    setTheme(resolved)
    document.documentElement.setAttribute('data-theme', resolved)
    document.documentElement.style.colorScheme = resolved
    try { localStorage.setItem(STORAGE_KEY, preference) } catch { /* ignore */ }
  }, [preference])

  React.useEffect(() => {
    if (preference !== 'auto') return

    const check = () => {
      const resolved = resolveTheme('auto')
      setTheme((prev) => {
        if (prev === resolved) return prev
        document.documentElement.setAttribute('data-theme', resolved)
        document.documentElement.style.colorScheme = resolved
        return resolved
      })
    }

    const interval = window.setInterval(check, 60_000)
    return () => window.clearInterval(interval)
  }, [preference])

  const setPreference = React.useCallback((pref: ThemePreference) => {
    setPreferenceState(pref)
  }, [])

  const toggleTheme = React.useCallback(() => {
    setPreferenceState((prev) => {
      if (prev === 'auto') {
        const current = resolveTheme('auto')
        return current === 'dark' ? 'light' : 'dark'
      }
      return prev === 'dark' ? 'light' : 'dark'
    })
  }, [])

  const value = React.useMemo(
    () => ({ theme, preference, setPreference, toggleTheme }),
    [theme, preference, setPreference, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
